import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  FormControlLabel,
  Checkbox,
  Grid,
  Card,
  CardContent
} from '@mui/material'
import useApi from '../hooks/useApi'
import moment from 'moment-timezone'
import { useState, useContext } from 'react'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import NiceModal from '@ebay/nice-modal-react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, Controller } from 'react-hook-form'
import { DateTimePicker } from '@mantine/dates'
import yup from '../lib/yup'
import haxios from '../lib/haxios'
import { ModalContainerAnimateDown } from './gsys-ui/ModalContainer/ModalContainer'
import { groupByKey, successToast } from '../lib/util'
import { GlobalContext } from '../context/GlobalContext'

const validation = yup.object().shape({
  heading: yup.string().required('Title is required'),
  subtitle: yup.string(),
  contents: yup.string(),
  image: yup
    .mixed()
    .nullable()
    .test('fileType', 'File must be an image (JPG or PNG)', (value) => {
      if (!value) return true // Allow null/undefined values
      return value.type === 'image/jpeg' || value.type === 'image/jpg' || value.type === 'image/png'
    }),
  playerIds: yup
    .array()
    .of(yup.string())
    .min(1, 'Select at least one user')
    .required('Select at least one user')
})

const Notifications = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [imageUrl, setImageUrl] = useState(null)
  const [submitError, setSubmitError] = useState(null)
  const { brandingBlob } = useContext(GlobalContext)

  const devices = useApi({
    url: '/garage/devices',
    initial: []
  })

  const devicesGrouped = groupByKey('Acct', devices.data)

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validation),
    defaultValues: {
      heading: '',
      subtitle: '',
      contents: '',
      image: null,
      playerIds: []
    }
  })

  const handleFileSelect = (file) => {
    setValue('image', file, { shouldValidate: true })
    if (file) {
      const url = URL.createObjectURL(file)
      setImageUrl(url)
    } else {
      setImageUrl(null)
    }
  }

  const onSubmit = async (data) => {
    // Form validation will prevent this function from being called if validation fails
    console.log('onSubmit called with data:', data)
    setIsLoading(true)
    setSubmitError(null)
    try {
      const formData = new FormData()
      formData.append('image', data.image)
      const imageResponse = data.image
        ? await haxios.post('/promotions/notificationImageUpload', formData)
        : null

      const json = {
        playerIds: data.playerIds,
        heading: data.heading,
        contents: data.contents,
        subtitle: data.subtitle,
        image: imageResponse?.url,
        data: {
          navigateTo: 'Notification',
          itemId: '12345'
        }
      }

      await haxios.post('/onesignal/sendNotification', json)

      successToast('Notification sent!')
      reset()
      setImageUrl(null)
    } catch (error) {
      console.error('Error uploading promotion:', error)
      setSubmitError('Failed to send notification. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box sx={{
      backgroundColor: '#2C3E50',
      minHeight: '100vh',
      p: 3
    }}>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          return handleSubmit(onSubmit)(e)
        }}
      >
        {submitError && (
          <Box sx={{
            mb: 2,
            p: 2,
            backgroundColor: 'rgba(231, 76, 60, 0.1)',
            border: '1px solid #E74C3C',
            borderRadius: 2,
            color: '#E74C3C',
            fontWeight: 600
          }}>
            {submitError}
          </Box>
        )}

        <Grid container spacing={3}>
          {/* Left Side - Form */}
          <Grid item xs={12} md={6}>
            <Card sx={{
              background: 'linear-gradient(135deg, #34495E 0%, #2C3E50 100%)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="heading"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Title"
                          fullWidth
                          error={!!errors.heading}
                          helperText={errors.heading?.message}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: '12px',
                              '& fieldset': {
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                              },
                              '&:hover fieldset': {
                                borderColor: '#FF6B35',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#FF6B35',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              color: 'white',
                            },
                            '& .MuiInputBase-input': {
                              color: 'white',
                            },
                            '& .MuiFormHelperText-root': {
                              color: '#E74C3C',
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="subtitle"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Subtitle (optional)"
                          fullWidth
                          error={!!errors.subtitle}
                          helperText={errors.subtitle?.message}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: '12px',
                              '& fieldset': {
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                              },
                              '&:hover fieldset': {
                                borderColor: '#FF6B35',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#FF6B35',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              color: 'white',
                            },
                            '& .MuiInputBase-input': {
                              color: 'white',
                            },
                            '& .MuiFormHelperText-root': {
                              color: '#E74C3C',
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="contents"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Contents"
                          multiline
                          rows={5}
                          fullWidth
                          error={!!errors.contents}
                          helperText={errors.contents?.message}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: '12px',
                              '& fieldset': {
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                              },
                              '&:hover fieldset': {
                                borderColor: '#FF6B35',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#FF6B35',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              color: 'white',
                            },
                            '& .MuiInputBase-input': {
                              color: 'white',
                            },
                            '& .MuiFormHelperText-root': {
                              color: '#E74C3C',
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="image"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          type="file"
                          inputProps={{ accept: "image/jpeg,image/jpg,image/png" }}
                          onChange={(e) => {
                            const file = e.target.files[0]
                            field.onChange(file)
                            handleFileSelect(file)
                          }}
                          label="Select Image"
                          fullWidth
                          error={!!errors.image}
                          helperText={errors.image?.message}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: '12px',
                              '& fieldset': {
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                              },
                              '&:hover fieldset': {
                                borderColor: '#FF6B35',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#FF6B35',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              color: 'white',
                            },
                            '& .MuiInputBase-input': {
                              color: 'white',
                            },
                            '& .MuiFormHelperText-root': {
                              color: '#E74C3C',
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 2 }}>
                      Select users
                    </Typography>
                    {errors.playerIds && (
                      <Typography variant="body2" sx={{ color: '#E74C3C', mb: 2 }}>
                        {errors.playerIds.message}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name="playerIds"
                      control={control}
                      render={({ field }) => (
                        <Box sx={{
                          height: '365px',
                          overflowY: 'auto',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          borderRadius: '12px',
                          p: 2,
                          border: '1px solid rgba(255, 255, 255, 0.2)'
                        }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={field.value.length === devices.data.length}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    field.onChange(devices.data.map((e) => e.OneSignalId))
                                  } else {
                                    field.onChange([])
                                  }
                                }}
                                sx={{
                                  color: '#FF6B35',
                                  '&.Mui-checked': {
                                    color: '#FF6B35',
                                  },
                                }}
                              />
                            }
                            label={
                              <Typography sx={{ color: 'white', fontWeight: 600 }}>
                                Select all
                              </Typography>
                            }
                            sx={{ mb: 2 }}
                          />
                          {Object.values(devicesGrouped).map((garage) => {
                            const garageAcct = garage[0].Acct
                            const garageName = garage[0].GarageName
                            const garageUsers = garage.map((u) => u.OneSignalId)

                            const garageIsSelected = (field) => {
                              return garageUsers.every((u) => field.value.includes(u))
                            }

                            return (
                              <Box key={garageAcct} sx={{ mb: 2 }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={garageIsSelected(field)}
                                      onChange={(e) => {
                                        let newUsers = [...field.value]

                                        if (e.target.checked) {
                                          garageUsers.forEach((osId) => {
                                            if (!newUsers.includes(osId)) {
                                              newUsers.push(osId)
                                            }
                                          })
                                        } else {
                                          newUsers = newUsers.filter((osId) => !garageUsers.includes(osId))
                                        }

                                        field.onChange(newUsers)
                                      }}
                                      sx={{
                                        color: '#FF6B35',
                                        '&.Mui-checked': {
                                          color: '#FF6B35',
                                        },
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography sx={{ color: 'white', fontWeight: 600 }}>
                                      [{garageAcct}] {garageName}
                                    </Typography>
                                  }
                                />
                                <Box sx={{ position: 'relative', pl: 6, pt: 1 }}>
                                  <Box sx={{
                                    position: 'absolute',
                                    width: '1px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                    left: '9px',
                                    top: '4px',
                                    bottom: '4px'
                                  }} />
                                  {garage.map((user) => (
                                    <Box key={user.OneSignalId} sx={{ py: 0.5 }}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={field.value.includes(user.OneSignalId)}
                                            onChange={(e) => {
                                              const updatedUsers = e.target.checked
                                                ? [...field.value, user.OneSignalId]
                                                : field.value.filter((b) => b !== user.OneSignalId)
                                              field.onChange(updatedUsers)
                                            }}
                                            sx={{
                                              color: '#FF6B35',
                                              '&.Mui-checked': {
                                                color: '#FF6B35',
                                              },
                                            }}
                                          />
                                        }
                                        label={
                                          <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                                            {user.GarageUserName}
                                          </Typography>
                                        }
                                      />
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            )
                          })}
                        </Box>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isLoading}
                      sx={{
                        backgroundColor: '#FF6B35',
                        color: 'white',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        borderRadius: '12px',
                        px: 4,
                        py: 1.5,
                        boxShadow: '0 2px 8px rgba(255, 107, 53, 0.3)',
                        '&:hover': {
                          backgroundColor: '#E55A2B',
                          boxShadow: '0 4px 12px rgba(255, 107, 53, 0.4)',
                        },
                        '&:disabled': {
                          backgroundColor: 'rgba(255, 107, 53, 0.5)',
                        },
                      }}
                    >
                      {isLoading ? 'Sending...' : 'Send'}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{
              background: 'linear-gradient(135deg, #34495E 0%, #2C3E50 100%)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 3 }}>
                  Preview
                </Typography>

                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 3,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '24px',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                  <Box sx={{ flexShrink: 0, mr: 3 }}>
                    <Box
                      component="img"
                      src={brandingBlob}
                      sx={{
                        width: '60px',
                        height: '60px',
                        objectFit: 'contain',
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        p: 1
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" sx={{
                      color: 'white',
                      fontWeight: 600,
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {watch('heading') || 'Notification Title'}
                    </Typography>
                    {watch('subtitle') && (
                      <Typography variant="subtitle1" sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontWeight: 500,
                        lineHeight: 1.2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {watch('subtitle')}
                      </Typography>
                    )}
                    <Typography variant="body1" sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {watch('contents') || 'Notification content will appear here...'}
                    </Typography>
                  </Box>

                  <Box sx={{ flexShrink: 0 }}>
                    {imageUrl && (
                      <Box
                        component="img"
                        src={imageUrl}
                        alt="Preview"
                        sx={{
                          width: '40px',
                          height: '40px',
                          objectFit: 'cover',
                          borderRadius: '8px'
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </form>
    </Box>
  )
}

export default Notifications
